import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { map } from 'lodash';
import PropTypes from 'prop-types';

import { Input } from '../../GlobalUI';

function GroupInfo({ onSubmit, fetching, history, dealers, group }) {
  const [id, setId] = useState();
  const [name, setName] = useState('');
  const [selectedDealer, setSelectedDealer] = useState({
    value: null,
    label: null,
  });
  const [groupDealers, setGroupDealers] = useState({});
  const [logo, setLogo] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoIsSet, setLogoIsSet] = useState();

  const [dealerHashMap, setDealerHashMap] = useState({});

  useEffect(() => {
    const { id: groupId, name: groupName, Dealers, config } = group;
    const { logo: _logoURL } = config || {};
    setId(groupId);
    setName(groupName);
    if (_logoURL) {
      setLogoUrl(_logoURL);
    }
    const groupDealersMap = {};
    if (Dealers?.length) {
      Dealers.forEach(({ id: dealerId, name: dealerLabel }) => {
        groupDealersMap[dealerId] = dealerLabel;
      });
    }

    setGroupDealers(groupDealersMap);
  }, [group]);

  const onChangeName = useCallback(e => setName(e.target.value), []);
  const onLogoUpload = useCallback(e => {
    setLogo(e?.target?.files[0]);
    setLogoIsSet(true);
  }, []);

  const onChangeSelectedDealer = useCallback(dealer => {
    setSelectedDealer(dealer);
  }, []);

  const onDealerAdd = useCallback(() => {
    if (!selectedDealer?.value) {
      return;
    }
    const newGroupDealers = { ...groupDealers };
    newGroupDealers[selectedDealer.value] = selectedDealer.label;
    setGroupDealers(newGroupDealers);
    setSelectedDealer({
      value: null,
      label: null,
    });
  }, [selectedDealer, groupDealers]);
  const onDealerRemove = useCallback(
    dealerId => {
      const newGroupDealers = { ...groupDealers };
      delete newGroupDealers[dealerId];
      setGroupDealers({ ...newGroupDealers });
    },
    [groupDealers],
  );

  const dealersMap = useMemo(() => {
    const dealermap = {};
    // optionsMap is going to be used in the select components
    const optionsMap = dealers
      .filter(({ id: dealerId, dealergroup_id: dealergroupId, ...dealer }) => {
        dealermap[dealerId] = {
          id: dealerId,
          dealergroup_id: dealergroupId,
          ...dealer,
        };
        // Filter to get options not already on my list and and dealers who don't have groups. The exception is dealers who have this group as their group to handle delete cases.
        return (
          !groupDealers[dealerId] && (!dealergroupId || dealergroupId === id)
        );
      })
      .map(({ id: dealerId, name: label }) => ({
        value: dealerId,
        label,
      }));
    // dealerHashMap will be used as a mapping tool later on when submitting the request
    setDealerHashMap(dealermap);
    return optionsMap;
  }, [dealers, groupDealers, id]);

  const handleSubmit = useCallback(
    async (e, goNext) => {
      e.preventDefault();
      if (!name) {
        return;
      }
      const nextRoute = `/group/${id}/setup/2`;
      const route = goNext ? nextRoute : null;
      const reader = new FileReader();
      if (logoIsSet) {
        await reader.readAsDataURL(logo);
        reader.onloadend = async () => {
          onSubmit({
            ...group,
            name,
            Dealers: map(
              groupDealers,
              (_, dealerId) => dealerHashMap[dealerId],
            ),
            logoFile: reader.result,
            route,
          });
        };
        return;
      }
      onSubmit({
        ...group,
        name,
        Dealers: map(groupDealers, (_, dealerId) => dealerHashMap[dealerId]),
        route,
      });
    },
    [id, name, groupDealers, dealerHashMap, onSubmit, group, logo, logoIsSet],
  );

  const renderButtonGroup = () => (
    <ButtonGroup>
      <Button
        bsClass="btn btn-primary btn-large"
        disabled={fetching}
        type={group?.id ? 'submit' : 'button'}
        onClick={group?.id ? null : () => history.push('/groups')}
      >
        {group?.id ? 'Save and Exit' : 'Cancel'}
      </Button>
      <Button
        bsClass="btn btn-primary btn-large"
        disabled={fetching}
        type="button"
        onClick={e => handleSubmit(e, true)}
      >
        Save {`>>`}
      </Button>
    </ButtonGroup>
  );

  const renderGroupDealers = useMemo(() => {
    const dealerLength = Object.keys(groupDealers).length;
    if (dealerLength < 1) {
      return <div className="group-no-dealers" />;
    }
    return (
      <div>
        <div className="group-dealers-count">
          {`${dealerLength} Dealer${dealerLength > 1 ? 's' : ''}`}
        </div>
        <div className="group-dealers">
          <div className="group-dealers-title">Name</div>
          {map(groupDealers, (dealerName, dealerId) => (
            <div className="group-dealer-item" key={dealerId}>
              <p>{dealerName}</p>
              <Button
                onClick={() => onDealerRemove(dealerId)}
                className="group-dealer-remove"
              >
                {'< Remove'}
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }, [groupDealers, onDealerRemove]);

  return (
    <div className="group-info-container">
      <form onSubmit={handleSubmit}>
        <div className="group-editor-row">
          {group?.id && <Input label={'ID'} value={id} readOnly />}

          <Input label={'Name'} value={name} onChange={onChangeName} />

          {group?.id && (
            <div>
              <Input
                label={'Logo'}
                type="file"
                onChange={onLogoUpload}
                placeholder={logo || logoUrl ? 'Change logo' : 'Upload a file'}
              />
              {(logo || logoUrl) && (
                <img
                  src={logo ? URL.createObjectURL(logo) : logoUrl}
                  className="group-editor-logo"
                  alt="logo"
                />
              )}
            </div>
          )}
        </div>
        {group?.id && (
          <div className="group-editor-row">
            <div className="group-editor-select">
              <Input
                label="Dealers"
                type="select"
                options={dealersMap}
                onChange={onChangeSelectedDealer}
                value={selectedDealer}
                className="group-dealer-select"
                containerClassName="group-dealer-select-container"
              />
              <Button
                bsClass="btn btn-primary btn-large group-dealer-add"
                onClick={onDealerAdd}
              >
                Add
              </Button>
            </div>

            {renderGroupDealers}
          </div>
        )}
        {renderButtonGroup()}
      </form>
    </div>
  );
}

GroupInfo.propTypes = {
  onSubmit: PropTypes.func,
  fetching: PropTypes.bool,
  history: PropTypes.object,
  dealers: PropTypes.array,
  group: PropTypes.object,
};

export default GroupInfo;
